import { useCallback, useEffect } from 'react';
import { invokeAsExtension } from '../../extensions/capabilities/enforce';
import { setDbManagerTabs, clearDbManagerTabAutoRun } from '../../store/tabsStore';
import { resolveAiDbRun } from '../../services/aiExtensionBridges';
import { DB_BATCH_COMMANDS, DB_QUERY_MULTI_COMMANDS, quoteIdentFor } from '../../extensions/dbEngines';
import {
  makeColumnCaster,
  buildUpdateStatements,
  buildInsertStatement,
  buildDeleteStatements,
} from '../dbGridSql';
import { isOpaqueCell } from '../dbCellTags';
import { cellToText } from '../dbCellText';
import { parseCellKey } from '../dbGridModel';
import { engineOf, tableRefFor, PENDING_ACTION_TTL_MS } from './dbResultsTabHelpers';

/** Saving, discarding, deleting and duplicating rows, context menus and dump import. */
export function useDbRowChanges({
  tab,
  result,
  isQueryTab,
  runFreeQuery,
  setResult,
  setError,
  setFailedSql,
  setWriteError,
  loading,
  pendingRows,
  setPendingRows,
  pendingEdits,
  setPendingEdits,
  pendingDeletes,
  setPendingDeletes,
  saving,
  setSaving,
  primaryKeyCols,
  setEditingCell,
  tableColumns,
  uniqueCols,
  setConfirmRequest,
  runTableQuery,
  selectedRows,
  setSelectedRows,
  selectedCells,
  setSelectedCells,
  selectionAnchorRef,
  setCellAnchor,
  setContextMenu,
  setExportOpen,
  setExportRows,
  pendingRowIdRef,
  handleQueryFailure,
  fetchRowCount,
  hasUnsavedGridChanges,
}) {
  const saveChanges = useCallback(() => {
    if (!tab?.tableName || !result || saving) return;
    const rowsToSave = pendingRows.filter((r) => r.values.some((v) => v.trim() !== ''));
    const doomed = new Set([...pendingDeletes].filter((i) => result.rows[i]));
    // An edit to a row that is about to be deleted is not worth a statement,
    // and issuing both would depend on the batch's internal order to be
    // harmless. Dropped here so the intent is explicit.
    const editEntries = Object.entries(pendingEdits).filter(
      ([key]) => !doomed.has(Number(key.split(':')[0]))
    );
    if (rowsToSave.length === 0 && editEntries.length === 0 && doomed.size === 0) {
      setPendingRows([]);
      setPendingEdits({});
      setPendingDeletes(new Set());
      return;
    }
    setSaving(true);
    setError(null);
    setFailedSql(null);
    setWriteError(null);

    // Snapshot the current selection as primary-key values (not indexes) so
    // the post-save refetch can re-locate the same logical rows even if the
    // edit reordered or dropped rows under the active sort/filter.
    let preserveSelectionKeys = null;
    if (selectedRows.size > 0 && primaryKeyCols.length > 0) {
      const pkIdx = primaryKeyCols.map((c) => result.columns.indexOf(c));
      if (pkIdx.every((i) => i !== -1)) {
        const keys = [];
        selectedRows.forEach((rowIndex) => {
          const row = result.rows[rowIndex];
          if (!row) return;
          keys.push(JSON.stringify(pkIdx.map((i) => cellToText(row[i]))));
        });
        preserveSelectionKeys = { pkCols: primaryKeyCols, keys };
      }
    }

    const engine = engineOf(tab);
    const tableRef = tableRefFor(engine, tab);
    // Postgres binds staged strings as TEXT params and won't coerce them to
    // the column type, so every placeholder carries a `::type` cast derived
    // from the discovered column metadata (no-op caster for mysql/sqlite).
    const castFor = makeColumnCaster(engine, tableColumns);

    const editsByRow = {};
    editEntries.forEach(([key, value]) => {
      const [rowIndex, colIndex] = key.split(':').map(Number);
      (editsByRow[rowIndex] = editsByRow[rowIndex] || []).push({ colIndex, value });
    });
    // DELETEs FIRST, deliberately. Every statement's WHERE is built from the
    // values in `result` as it stands right now, so a delete has to run before
    // any UPDATE that could change the very column its WHERE matches on —
    // editing a primary key and deleting another row in the same save would
    // otherwise have the delete miss. Nothing depends on the reverse order.
    // A row the builders cannot address exactly (no key + an unreadable value)
    // refuses the whole save with the reason, instead of an uncaught throw.
    let statements;
    try {
      statements = doomed.size > 0
        ? buildDeleteStatements({ engine, tableRef, result, primaryKeyCols, rowIndexes: doomed, castFor })
        : [];
      statements.push(...buildUpdateStatements({ engine, tableRef, result, primaryKeyCols, editsByRow, castFor }));
    } catch (e) {
      handleQueryFailure(tab, e, { isWrite: true });
      setSaving(false);
      return;
    }

    if (rowsToSave.length > 0) {
      statements.push(...buildInsertStatement({ engine, tableRef, columns: result.columns, rowsToSave, castFor }));
    }

    // One atomic transaction on a single connection (Rust `*_execute_batch`):
    // either every edit + insert lands, or none do. The old sequential query
    // chain could half-commit — pooled query calls can't share a BEGIN.
    invokeAsExtension(DB_BATCH_COMMANDS[engine], { extensionId: tab.extensionId, connId: tab.connId, statements })
      .then(() => {
        // Swap the just-saved values into the grid in place first, then
        // reconcile with a silent refetch (picks up DB-generated values like
        // autoincrement ids and column defaults) — never through the Loading
        // empty state, which unmounts the grid and resets its scroll.
        setResult((prev) => {
          if (!prev) return prev;
          const rows = prev.rows
            .map((row, i) => {
              const edits = editsByRow[i];
              if (!edits) return row;
              const next = [...row];
              for (const e of edits) next[e.colIndex] = e.value;
              return next;
            })
            // Deleted rows leave in the same pass, so the optimistic view
            // matches what the batch just did and the refetch has nothing
            // surprising to reconcile.
            .filter((_, i) => !doomed.has(i));
          for (const r of rowsToSave) rows.push(r.values.map((v) => (v.trim() === '' ? null : v)));
          return { ...prev, rows };
        });
        setPendingRows([]);
        setPendingEdits({});
        setPendingDeletes(new Set());
        if (doomed.size > 0) {
          // Deletion renumbers everything after the first gap, and both
          // selections are index-based — keeping them would point at whatever
          // slid into those slots.
          setSelectedRows(new Set());
          selectionAnchorRef.current = null;
          setSelectedCells(new Set());
          setCellAnchor(null);
          setEditingCell(null);
        }
        // preserveSelection: keeps the selection (and the transposed view,
        // which lives off it) valid through this refetch instead of snapping
        // back to horizontal. Re-located by primary-key value, not index —
        // an edit to a sorted/filtered column can reorder or drop rows.
        // Refetch under the COMMITTED filter (tab.filterText), never the draft
        // in the input: an un-run draft must not silently change which rows
        // come back — and export reads the committed value too, so the two
        // would otherwise disagree about what the grid is showing.
        runTableQuery(tab, tab.filterText || '', { silent: true, preserveSelection: true, preserveSelectionKeys });
        fetchRowCount(tab, tab.filterText || '');
      })
      .catch((e) => handleQueryFailure(tab, e, { isWrite: true }))
      .finally(() => setSaving(false));
  }, [tab, result, pendingRows, pendingEdits, pendingDeletes, primaryKeyCols, tableColumns, saving, selectedRows, runTableQuery, fetchRowCount, handleQueryFailure, setPendingRows, setPendingEdits, setPendingDeletes, setCellAnchor, setSaving, setError, setFailedSql, setWriteError, setResult, setSelectedRows, selectionAnchorRef, setSelectedCells, setEditingCell]);

  // Discards every pending edit and staged new row for the current tab
  // without touching the DB — the Cancel button, and Escape when no single
  // cell is actively being edited (that case is cancelEditCell's job).
  const discardChanges = useCallback(() => {
    setEditingCell(null);
    setPendingEdits({});
    setPendingRows([]);
    setPendingDeletes(new Set());
    // A stale save error belongs to the edits being discarded here.
    setWriteError(null);
  }, [setPendingEdits, setPendingRows, setPendingDeletes, setEditingCell, setWriteError]);

  // DELETE for a set of grid row indexes — same atomic batch as saveChanges,
  // same WHERE-by-PK (or all-columns fallback) row addressing, optimistic
  // in-place removal + silent reconcile so scroll survives. Reached from the
  // selection toolbar button and the grid context menu's "Delete row".
  // window.confirm/alert are Tauri dialog-plugin commands the app's
  // capabilities don't allow ("dialog.confirm not allowed"), so destructive
  // grid actions confirm through the shared ConfirmModal instead.
  /**
   * Mark rows for deletion. Nothing is sent — Save is the confirmation.
   *
   * This used to open a confirm dialog and delete immediately, which made
   * deletion the one destructive action with a completely different shape from
   * every other change in this grid: edits and inserts stage, show in the
   * footer count, and commit on Save or vanish on Escape. Deletes now do the
   * same, and Save is one atomic batch with the rest, so a delete plus an edit
   * either both land or neither does.
   *
   * That also retires the "your staged edits will be discarded" warning the
   * dialog carried: they are no longer discarded, they are saved alongside.
   */
  const stageDeleteRows = useCallback((rowIndexes) => {
    if (!tab?.tableName || !result || saving || !rowIndexes || rowIndexes.size === 0) return;
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      // Only rows that actually exist in the current result — a stale index
      // would build a WHERE from `undefined` at save time.
      rowIndexes.forEach((i) => {
        if (result.rows[i]) next.add(i);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [tab, result, saving, setPendingDeletes]);

  /** Un-mark rows — the same key/button toggles a marked row back. */
  const unstageDeleteRows = useCallback((rowIndexes) => {
    setPendingDeletes((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      rowIndexes.forEach((i) => next.delete(i));
      return next.size === prev.size ? prev : next;
    });
  }, [setPendingDeletes]);

  /**
   * The rows a ROW-level gesture acts on: the selected rows, or — when only
   * cells are selected — the rows those cells sit in.
   *
   * Shared by Delete and Ctrl+D, which is the point. Both mean "this record",
   * so both should reach it the same way; Ctrl+D used to require a row
   * selection and did nothing at all from a cell, which made two keys that
   * describe the same target disagree about what counted as picking it.
   * Clearing a VALUE is Ctrl+X's job, and it is the one that stays cell-level.
   *
   * Pending rows are excluded — they exist only in the staging area, and the ✕
   * on the row is how those go.
   */
  const rowsForRowGesture = useCallback(() => {
    if (selectedRows.size > 0) return selectedRows;
    const rows = new Set();
    for (const key of selectedCells) {
      const { kind, id } = parseCellKey(key);
      if (kind === 'r') rows.add(id);
    }
    return rows;
  }, [selectedRows, selectedCells]);

  /**
   * Toggle: a gesture over rows that are ALL already marked un-marks them.
   * Delete is the only way in and the only way out, so hitting it twice on the
   * same selection leaves nothing behind — the alternative is a mark you can
   * only clear by discarding every other staged change too.
   */
  const toggleDeleteMark = useCallback(() => {
    const rows = rowsForRowGesture();
    if (rows.size === 0) return;
    const allMarked = [...rows].every((i) => pendingDeletes.has(i));
    if (allMarked) unstageDeleteRows(rows);
    else stageDeleteRows(rows);
  }, [rowsForRowGesture, pendingDeletes, stageDeleteRows, unstageDeleteRows]);

  const deleteSelectedRows = useCallback(() => toggleDeleteMark(), [toggleDeleteMark]);

  // What the footer button says it will act on, and which way it will go.
  const deleteGestureRows = rowsForRowGesture();
  const deleteGestureCount = deleteGestureRows.size;
  const deleteGestureAllMarked =
    deleteGestureCount > 0 && [...deleteGestureRows].every((i) => pendingDeletes.has(i));

  // Context menu "Duplicate row" / Ctrl+D on a selection: stages pending-row
  // copies of each row's effective (pending-edit-aware) values, with
  // primary-key/autoincrement columns omitted when the PK is known (so the
  // insert generates fresh keys) and unique-constrained columns omitted
  // always (a copied unique value would fail the insert). These columns are
  // left out of the INSERT's column list entirely (via `omitCols`) rather
  // than blanked to an empty string: an explicit NULL parameter overrides a
  // column's DEFAULT expression, which would violate NOT NULL on a typical
  // serial/uuid primary key or a `UNIQUE NOT NULL` column.
  const duplicateRows = useCallback((rowIndexes) => {
    if (!result) return;
    const indexes = Array.from(rowIndexes).filter((i) => result.rows[i]).sort((a, b) => a - b);
    if (indexes.length === 0) return;
    const omitSet = new Set(uniqueCols);
    if (primaryKeyCols.length > 0) primaryKeyCols.forEach((c) => omitSet.add(c));
    const additions = indexes.map((rowIndex) => {
      const id = ++pendingRowIdRef.current;
      // Per-row, because which columns are unreadable is a property of the row
      // being copied, not of the table.
      const omit = new Set(omitSet);
      const values = result.rows[rowIndex].map((v, j) => {
        if (omitSet.has(result.columns[j])) return '';
        const key = `${rowIndex}:${j}`;
        const eff = Object.prototype.hasOwnProperty.call(pendingEdits, key) ? pendingEdits[key] : v;
        // A blob or an undecodable cell has no text form worth copying —
        // cellToText would hand the INSERT a base64 blob or a type name. Leave
        // the column out and let the database apply its default.
        if (isOpaqueCell(eff)) {
          omit.add(result.columns[j]);
          return '';
        }
        return cellToText(eff);
      });
      // Where the copy is DRAWN: directly under the row it came from, rather
      // than at the bottom with the Add-row inserts. Duplicating row 12 of a
      // thousand and having the copy appear off-screen reads as nothing having
      // happened. See groupPendingRows.
      return { id, values, omitCols: omit, afterRow: rowIndex };
    });
    setPendingRows((prev) => [...prev, ...additions]);
  }, [result, primaryKeyCols, uniqueCols, pendingEdits, setPendingRows, pendingRowIdRef]);

  const duplicateRow = useCallback((rowIndex) => duplicateRows([rowIndex]), [duplicateRows]);
  const duplicateSelectedRows = useCallback(
    () => duplicateRows(rowsForRowGesture()),
    [duplicateRows, rowsForRowGesture]
  );

  const openContextMenu = useCallback((e, rowIndex, colIndex) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, rowIndex, colIndex, hover: -1 });
  }, [setContextMenu]);

  // Right-click on a column header: column options. `column` (rather than a
  // colIndex) marks this as the header menu, so the same portal renderer can
  // tell the two apart.
  const openHeaderContextMenu = useCallback((e, column) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, rowIndex: null, colIndex: null, column, hover: -1 });
  }, [setContextMenu]);

  // Right-click on the grid background (no cell): table-level menu only
  // (Export/Import). Cells preventDefault before the event bubbles here, so a
  // claimed right-click is skipped.
  const openBackgroundContextMenu = useCallback((e) => {
    if (e.defaultPrevented) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, rowIndex: null, colIndex: null, hover: -1 });
  }, [setContextMenu]);

  // "Import SQL dump…": pick a .sql file and run it verbatim as one
  // multi-statement script on a single connection (the raw-sql *_query_multi
  // path — same one the script editor uses). Works for INSERT-style dumps
  // (mysqldump, sqlite3 .dump, pg_dump --inserts); plain pg_dump COPY-FROM-
  // stdin blocks are not supported by the simple-protocol path.
  const importDump = useCallback(async () => {
    if (!tab?.tableName || loading || saving) return;
    // The dump swaps the row set and pending edit keys are row-index-based
    // (same invariant the pager enforces). Alert rather than silently return:
    // this is also invoked headlessly from the pendingAction effect, where a
    // silent no-op would be invisible.
    if (hasUnsavedGridChanges) {
      // eslint-disable-next-line no-alert
      setConfirmRequest({
        title: 'Unsaved changes',
        message: 'Save or discard pending changes before importing a dump.',
        confirmText: 'OK',
        hideCancel: true,
        onConfirm: () => {},
      });
      return;
    }
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const path = await open({
        multiple: false,
        filters: [{ name: 'SQL dump', extensions: ['sql', 'dump', 'txt'] }],
      });
      if (typeof path !== 'string' || !path) return;
      const { readTextFile } = await import('@tauri-apps/plugin-fs');
      const sql = await readTextFile(path);
      if (!sql.trim()) {
        setError('The selected file is empty.');
        return;
      }
      const fileName = path.split(/[\\/]/).pop();
      const sizeKb = (sql.length / 1024).toFixed(1);
      setConfirmRequest({
        title: 'Run SQL dump',
        message: `Run every statement in "${fileName}" (${sizeKb} KB) against ${tab.dbName ? `"${tab.dbName}"` : 'the connected database'}? The dump executes as-is and cannot be undone.`,
        confirmText: 'Run dump',
        type: 'danger',
        onConfirm: async () => {
          const engine = engineOf(tab);
          // Same database context as the free-query path: pooled connections
          // carry no session default, and the whole dump runs on one connection,
          // so the Rust side executes contextSql first and the context sticks.
          let contextSql = null;
          if (tab.dbName && engine === 'mysql') {
            contextSql = `USE ${quoteIdentFor('mysql', tab.dbName)}`;
          } else if (tab.dbName && engine === 'postgres') {
            contextSql = `SET search_path TO ${quoteIdentFor('postgres', tab.dbName)}`;
          }
          setSaving(true);
          setError(null);
          setFailedSql(null);
    setFailedSql(null);
          try {
            await invokeAsExtension(DB_QUERY_MULTI_COMMANDS[engine], { extensionId: tab.extensionId, connId: tab.connId, sql, params: [], contextSql });
            runTableQuery(tab, tab.filterText || '');
            fetchRowCount(tab, tab.filterText || '');
          } catch (e) {
            // A failed dump execution is a write/DDL failure, not a read
            // failure — isWrite:true keeps the still-valid grid visible with
            // a banner, instead of blanking it via the plain `error` path.
            handleQueryFailure(tab, e, { isWrite: true });
          } finally {
            setSaving(false);
          }
        },
      });
    } catch (e) {
      handleQueryFailure(tab, e);
    }
  }, [tab, loading, saving, hasUnsavedGridChanges, runTableQuery, fetchRowCount, handleQueryFailure, setConfirmRequest, setError, setSaving, setFailedSql]);

  // One-shot pendingAction planted by the sidebar tree's Export…/Import
  // items (rides sdk.ui.openTable → openDbManagerTab). Waits for the tab's
  // first result page — the export modal wants data for its "current result"
  // scope and importDump no-ops while loading — then consumes and clears.
  // Actions older than the TTL are dropped, not fired: an action armed on a
  // tab that errored (or that the user navigated away from) must not pop a
  // file picker at some unrelated later moment when a result finally loads.
  useEffect(() => {
    if (isQueryTab || !tab?.pendingAction || loading || !result) return;
    const action = tab.pendingAction;
    const stale = Date.now() - (tab.pendingActionAt || 0) > PENDING_ACTION_TTL_MS;
    setDbManagerTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, pendingAction: undefined, pendingActionAt: undefined } : t)));
    if (stale) {
      console.warn(`[db-manager] dropped stale pending ${action} action`);
      return;
    }
    if (action === 'export') { setExportRows(null); setExportOpen(true); } // from the tree: no selection
    else if (action === 'import') importDump();
  }, [tab, isQueryTab, loading, result, importDump, setExportRows, setExportOpen]);

  // One-shot AI auto-run: the assistant's db_run_sql tool opens a query tab with
  // autoRun + aiRunId set. Run it once through the grid, resolve the AI bridge
  // with metadata ONLY (row count + column names — never row values), then clear
  // the flags so it never re-fires.
  useEffect(() => {
    if (!tab?.autoRun || !tab?.aiRunId || !isQueryTab) return;
    const runId = tab.aiRunId;
    const sql = tab.sql || '';
    clearDbManagerTabAutoRun(tab.id);
    Promise.resolve(runFreeQuery(tab, sql)).then((meta) => {
      resolveAiDbRun(runId, meta || { executed: true });
    });
  }, [tab, isQueryTab, runFreeQuery]);

  return {
    saveChanges,
    discardChanges,
    stageDeleteRows,
    unstageDeleteRows,
    toggleDeleteMark,
    deleteSelectedRows,
    deleteGestureCount,
    deleteGestureAllMarked,
    duplicateRow,
    duplicateSelectedRows,
    openContextMenu,
    openHeaderContextMenu,
    openBackgroundContextMenu,
    importDump,
  };
}
